// ============================================================================
// ConversationList — Sidebar conversation list
// ============================================================================

import { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import { Delete, Psychology } from '@mui/icons-material'
import { beoflowColors, typography, spacing, borderRadius, opacity, layout } from '@/theme/muiTheme'
import {
  useConversations,
  useDeleteConversation,
} from '@/services/api/chat'

interface ConversationListProps {
  activeId: number | null
  targetProfileId?: number | null
  emptyLabel?: string
  hideActive?: boolean
  onSelect: (id: number) => void
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' })
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

export default function ConversationList({
  activeId,
  targetProfileId,
  emptyLabel = 'No conversations yet.',
  hideActive = false,
  onSelect,
}: ConversationListProps) {
  const { data: conversations, isLoading, isError } = useConversations(targetProfileId)
  const deleteConversation = useDeleteConversation()
  const visibleConversations = hideActive ? conversations?.filter(conv => conv.id !== activeId) : conversations
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

  const handleDelete = () => {
    if (deleteTarget !== null) {
      deleteConversation.mutate(deleteTarget)
      setDeleteTarget(null)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: spacing.sm }}>
        {isLoading && !isError ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: spacing.xl }}>
            <CircularProgress size={24} color="info" />
          </Box>
        ) : !visibleConversations?.length ? (
          <Box sx={{ textAlign: 'center', py: spacing.xl, px: spacing.sm }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary' }}
            >
              {emptyLabel}
            </Typography>
          </Box>
        ) : (
          visibleConversations.map(conv => {
            const isActive = conv.id === activeId

            return (
              <Box
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                sx={{
                  p: spacing.sm,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  transition: 'background-color 0.15s ease',
                  backgroundColor: isActive
                    ? `${beoflowColors.meanBlue}${opacity.interactive}`
                    : 'transparent',
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  '&:hover': {
                    backgroundColor: isActive
                      ? `${beoflowColors.meanBlue}${opacity.border}`
                      : 'action.hover',
                  },
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: layout.chatAvatarSize,
                    height: layout.chatAvatarSize,
                    borderRadius: borderRadius.round,
                    backgroundColor: beoflowColors.graphGreyDark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Psychology sx={{ fontSize: 20, color: beoflowColors.meanBlue }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Title + date row */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: spacing.xs }}>
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: typography.fontSizeSm,
                        fontWeight: isActive
                          ? typography.fontWeightSemibold
                          : typography.fontWeightNormal,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {conv.title || 'New conversation'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: typography.fontFamilyMonospace,
                        fontSize: typography.fontSizeTiny,
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {formatDate(conv.updated_at)}
                    </Typography>
                  </Box>
                  {/* Preview text */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: typography.fontSizeTiny,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mt: '2px',
                      }}
                    >
                      {conv.last_message || 'Empty conversation'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        setDeleteTarget(conv.id)
                      }}
                      sx={{
                        p: '2px',
                        color: beoflowColors.graphGreyDark,
                        opacity: 0,
                        '.MuiBox-root:hover > &, &:focus': {
                          opacity: 1,
                        },
                        '&:hover': {
                          color: beoflowColors.redBase,
                          opacity: 1,
                        },
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )
          })
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete conversation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This conversation will be archived and hidden from your list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteConversation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
